// Tax Computation Interface Definitions
// Corresponds to Java VO classes in org.zhuo_yi.audit.entity.VO.tax_com

// ================= Sub VO Interfaces =================

// TaxBasicProfitsVO - Base class for tax computations
export interface TaxBasicProfitsVO {
    // LOSS
    perIncomeStatement?: number;
    attachedHereWith?: number;

    // ADD
    interestExpense?: number;
    penaltiesFinesStampDuties?: number;
    donationCharitable?: number;
    companySetUpFee?: number;
    depreciationExpenses?: number;
    lossDisposalOfMachineryPlant?: number;
    provisionDoubtful?: number;
    /** Exchange loss (Capital in nature); PL-OI-EXG − PL-AE-EXL when net < 0 */
    exchangeLoss?: number;
    balancingCharge?: number;
    /** ADD: full WHT from R14 / PL-AE-WHT */
    withholdingTax?: number;

    // LESS
    netInterestIncome?: number;
    /** LESS: PL-OI-SFG WP bank column; negated for LESS */
    subsidiesFromGovernment?: number;
    purchaseCostHardSoftware?: number;
    gainDisposalOfFixedAssets?: number;
    capitalExpenditure?: number;
    depreciation?: number;
    /** Dividend income (PL-OI-DI / QXI2 audit round); negated for LESS */
    dividendIncome?: number;
    industrialBuilding?: number;
    commercialBuilding?: number;
    /** Exchange gain (Capital in nature); PL-OI-EXG − PL-AE-EXL when net >= 0 (negated for LESS) */
    exchangeGain?: number;
    balancingAllowance?: number;
    /** LESS: excess WHT not eligible for tax credit (@ a−b) from R14; typically negative */
    excessWithholdingTax?: number;

    // Sum of less
    lessTotal?: number;

    scheduleAddBacks?: ScheduleAddBackItem[];

    /** Schedule Less Back rows (tax_section=LESS; amounts typically negative) */
    scheduleLessBacks?: ScheduleAddBackItem[];

    /** User-defined ADD lines (positive; 0 allowed for MULTI seed) */
    extraAddItems?: TaxExtraItem[];

    /** User-defined LESS lines (negative; 0 allowed for MULTI seed) */
    extraLessItems?: TaxExtraItem[];
}

export interface ScheduleAddBackItem {
    nameEn?: string;
    displayName?: string;
    finalTotal?: number;
    addBackPercent?: number;
    addBackAmount?: number;
    /** e.g. (HKD298,380 x 2/3 = HKD198,920) */
    formula?: string;
    /** ADD | LESS */
    taxSection?: string;
}

export interface TaxExtraItem {
    id?: number;
    /** Frontend-only stable key for unsaved rows */
    clientKey?: string;
    section?: 'ADD' | 'LESS' | string;
    itemName?: string;
    amount?: number;
    sortNo?: number;
}

// TaxBalancingAllowance
export interface TaxBalancingAllowance {
    rowName?: string;
    tenPool?: number;
    twentyPool?: number;
    thirtyPool?: number;
    hpThirtyPool?: number;
    balancingCharge?: number;
    balancingAllowance?: number;
    allowance?: number;
}

// TaxCostPerBook
export interface TaxCostPerBook {
    date?: string;
    equipmentName?: string;
    ppeId?: number;
    propertyId?: number;
    costPerBook?: number;
    stampDuty?: number;
    commissionLegalFee?: number;
    netValue?: number;
}

// TaxCommercialBuilding
export interface TaxCommercialBuilding {
    priorLocationsProperties?: TaxCostPerBook[];
    yearLocationsProperties?: TaxCostPerBook[];
    priorCostProperties?: number;
    yearCastProperties?: number;
    reducingValueBf?: number;
    wdvBf?: number;
    addDuringTheYear?: number;  // equal yearCastProperties
    bfSum?: number;  // reducingValueBf + addDuringTheYear
    annualAllowance?: number;  // -min(ROUND(costPropertiesSum * 0.04), bfSum)
    totalAllowance?: number;  // abs(annualAllowance) or balancing charge on disposal
    reducingValueCf?: number;  // max(0, bfSum - annual allowance)
    subtotalAfterAllowance?: number;  // bfSum - annual allowance, before sales proceed
    salesProceed?: number;  // negative HK$/2 when location of property disposed
    balancingCharge?: number;
    subtotalAfterSales?: number;  // subtotalAfterAllowance + salesProceed
    fullyUsedInPriorYears?: number;  // 0/1 - tax allowance fully used in prior years
    /** User override for prior-years cost label (null = calculated title) */
    commercialPriorLabel?: string | null;
    /** User override for prior-years cost value HK$/2 (null = calculated) */
    commercialPriorValue?: number | null;
    /** Leasehold prior-years label override */
    leaseholdPriorLabel?: string | null;
    /** Leasehold prior-years value override (full scale) */
    leaseholdPriorValue?: number | null;
}

// TaxMotorVehicle
export interface TaxMotorVehicle {
    date?: string;
    vehicleName?: string;
    cost?: number;
    accumulated?: number;
    nbv?: number;
    salesProceed?: number;
    gainOnDisposal?: number;
    lossOnDisposal?: number;
}

// TaxMotorHirePurchase
export interface TaxMotorHirePurchase {
    no?: string;
    date?: string;
    vehicleName?: string;
    cost?: number;
    hpBf?: number;
    downPayment?: number;
    hpAmount?: number;
    repaymentsForPeriod?: number;
    hpCf?: number;
    ia?: number;
}

// TaxNonCurrentAsset (P1.2 sub value)
export interface TaxNonCurrentAsset {
    rowName?: string;
    cost?: number;
    tenPool?: number;
    twentyPool?: number;
    thirtyPool?: number;
    hardSoftware?: number;
}

// TaxPpe
export interface TaxPpe {
    date?: string;
    equipmentName?: string;
    value?: number;
}

// ProfitsTaxAVO (P1a) - Offshore profit or Inactive loss
export interface ProfitsTaxAVO {
    // Add: Logic (annual(is it offshore claim = 1))
    // true = Offshore profit, false = Inactive loss
    addName?: string;
    addValue?: number;

    // Assessable profit
    assessmentName?: string;
    assessmentValue?: number;

    // Final assessment
    taxLossBf?: number;
    adjustedAssessmentName?: string;
    adjustedAssessment?: number;
    taxLossCf?: number;

    // Tax rate
    exchangeColumn?: string;
    afterRateValue?: number;

    note?: string;
    confirm?: string;
}

// ProfitsTaxBVO (P1b) - Simple Loss with Donations
export interface ProfitsTaxBVO {
    // Less:
    donationMadeValue?: number;

    // Adjusted Loss
    assessmentName?: string;
    assessmentValue?: number;

    // Statement of loss
    taxLossBf?: number;
    adjustedAssessmentName?: string;
    adjustedAssessment?: number;
    taxLossCf?: number;

    provisionalTaxPayment?: number;

    note?: string;
    confirm?: string;
}

// ProfitsTaxCVO (P1c) - Complete Tax Calculation
export interface ProfitsTaxCVO {
    // Less:
    donationMadeValue?: number;

    // Adjusted Loss
    assessmentName?: string;
    assessmentValue?: number;

    assessableName?: string;
    assessableValue?: number;

    taxLossBf?: number;
    taxLossCf?: number;

    // Final assessment xxxx/xx and provisional payment xxxx+1/xx+1
    finalProvisionalTitle?: string;

    assessableProfitName?: string;
    assessableProfit?: number;

    firstLevelTaxName?: string;
    firstLevelTaxValue?: number;
    afterFirstLevelTaxName?: string;
    afterFirstLevelTaxValue?: number;
    minTaxName?: string;
    minTaxValue?: number;
    taxSum?: number;

    // Provisional payment (next year)
    nextYearAssessableName?: string;
    nextYearAssessableValue?: number;
    nextYearTaxLossCf?: number;
    provisionalPaymentSum?: number;

    nextYearFirstLevelTaxName?: string;
    nextYearFirstLevelTaxValue?: number;
    nextYearAfterFirstLevelTaxName?: string;
    nextYearAfterFirstLevelTaxValue?: number;
    nextYearMinTaxName?: string;
    nextYearMinTaxValue?: number;
    nextYearTaxSum?: number;

    // firstLevelTaxSum + nextYearFirstLevelTaxSum
    totalTaxPayable?: number;

    provisionalTaxPayment?: number;

    // totalTaxPayable - provisionalTaxPayment
    sum?: number;

    note?: string;
    confirm?: string;
}

// Tax Computation Type Enum
export enum TaxComputationType {
    TYPE_A = 'TYPE_A',
    TYPE_B = 'TYPE_B',
    TYPE_C = 'TYPE_C'
}

// ================= Main VO Interfaces =================

// TaxComputationResult - Unified wrapper that extends TaxBasicProfitsVO
export interface TaxComputationResult extends TaxBasicProfitsVO {
    type?: TaxComputationType;
    profitsTaxA?: ProfitsTaxAVO;
    profitsTaxB?: ProfitsTaxBVO;
    profitsTaxC?: ProfitsTaxCVO;
}

// TaxDepreciationAllowanceScheduleVO (P1.1)
export interface TaxDepreciationAllowanceScheduleVO {
    // Depreciation allowance Schedule
    balancingList?: TaxBalancingAllowance[];

    // Commercial Building allowance Schedule
    commercialBuilding?: TaxCommercialBuilding;
    thisYearCommercialBuilding?: TaxCommercialBuilding;
    industrialBuilding?: TaxCommercialBuilding;
}

/** One row of P1.1a / Excel P1.1 (2) BREAKDOWN OF WITHHOLDING TAX */
export interface TaxWithholdingRow {
    incomeNature?: string;
    jurisdiction?: string;
    incomeAmount?: number;
    /** Decimal rate, e.g. 0.10 for 10% */
    foreignTaxRate?: number;
    withholdingTaxAmount?: number;
    unilateralEligible?: boolean;
}

/** Unilateral tax credit block on P1.1a; rate follows case: &lt;HK$2m → 8.25%, else 16.5% */
export interface TaxUnilateralCredit {
    eligibleIncomeSum?: number;
    /** Decimal rate from case Tax Deduction Limit (0.0825 or 0.165) */
    rate?: number;
    profitsTaxOnEligibleIncome?: number;
    withholdingTaxPaid?: number;
    creditLimit?: number;
    excessWithholdingTax?: number;
}

/** TaxWithholdingScheduleVO (P1.1a) — Excel P1.1 (2) */
export interface TaxWithholdingScheduleVO {
    rows?: TaxWithholdingRow[];
    totalWithholdingTax?: number;
    unilateralCredit?: TaxUnilateralCredit;
}

// TaxAdditionsNonCurrentAssetsVO (P1.2)
export interface TaxAdditionsNonCurrentAssetsVO {
    taxNonCurrentAssets?: TaxNonCurrentAsset[];
    taxMotorVehicles?: TaxMotorVehicle[];
}

// TaxObligationUnderFinanceLeasesVO (P1.21)
export interface TaxObligationUnderFinanceLeasesVO {
    motorVehicleHirePurchase?: TaxMotorHirePurchase[];
    taxMotorVehicles?: TaxMotorVehicle[];
}

// TaxPpeDetailsVO (P1.3)
export interface TaxPpeDetailsVO {
    liftEscalators?: TaxPpe[];
    furnitureFixture?: TaxPpe[];
    computerHardwareSoftware?: TaxPpe[];
    motorVehicle?: TaxPpe[];
    machinery?: TaxPpe[];
    equipments?: TaxPpe[];
}

/**
 * 合并 queryTaxByAnnualId / queryTaxSubP1 / queryTaxSubP11a / queryTaxSubP2 / queryTaxSubP21 / queryTaxSubP3
 * 的输出结果。当公司 principalActivities == 0（Inactive）或子表无数据时，对应字段为 null/undefined。
 */
export interface TaxComputationCombinedVO {
    taxComputationResult?: TaxComputationResult;
    p11Data?: TaxDepreciationAllowanceScheduleVO;
    /** P1.1a withholding tax (Excel P1.1 (2)) */
    p11aData?: TaxWithholdingScheduleVO;
    p12Data?: TaxAdditionsNonCurrentAssetsVO;
    p121Data?: TaxObligationUnderFinanceLeasesVO;
    p13Data?: TaxPpeDetailsVO;
}

// ================= BO Interfaces =================

// AuditTaxComPropertyBO
export interface AuditTaxComPropertyBO {
    id?: number;
    annualId?: number;
    ppeId?: number;
    stampDuty?: number;
    clFee?: number;
    propertyType?: number;  // 0 = prior year(priorLocationsProperties), 1 = current year(yearLocationsProperties)
    /** Sub-period YOA; empty = legacy/SINGLE */
    yearOfAssessment?: string;
}

/** Save payload for P1 user-defined ADD/LESS lines */
export interface AuditTaxComExtraItemBO {
    id?: number;
    companyId?: number;
    annualId?: number;
    yearOfAssessment?: string;
    section?: 'ADD' | 'LESS' | string;
    itemName?: string;
    amount?: number;
    sortNo?: number;
}

// AuditTaxComBO - Request model for save/update
export interface AuditTaxComBO {
    id?: number;
    companyId: number;
    annualId: number;
    /** Sub-period YOA; empty = legacy/SINGLE */
    yearOfAssessment?: string;
    taxLossBf?: number;
    taxPayment?: number;
    properties?: AuditTaxComPropertyBO[];
    /** null = skip sync; [] = clear section */
    extraAddItems?: AuditTaxComExtraItemBO[] | null;
    /** null = skip sync; [] = clear section */
    extraLessItems?: AuditTaxComExtraItemBO[] | null;
    oneReducingBf?: number;
    // P1.1 data - W.D.V. b/f pools
    wdvBfTenPool?: number;
    wdvBfTwentyPool?: number;
    wdvBfThirtyPool?: number;
    wdvBfHpThirtyPool?: number;
    // P1.1 data - Balancing allowance pools
    baTenPool?: number;
    baTwentyPool?: number;
    baThirtyPool?: number;
    baHpThirtyPool?: number;

    wdvBf?: number;

    // Fully used in prior years flags
    commercialFullyUsedPriorYears?: number;  // 0/1
    leaseholdFullyUsedPriorYears?: number;  // 0/1

    // P1 Type A note & confirm (auto-generated default, user-editable)
    p1Note?: string;
    p1Confirm?: string;

    /** Commercial prior-years cost label override; null clears DB value */
    commercialPriorLabel?: string | null;
    /** Commercial prior-years cost value override (HK$/2); null clears DB value */
    commercialPriorValue?: number | null;
    /** Leasehold prior-years label override; null clears DB value */
    leaseholdPriorLabel?: string | null;
    /** Leasehold prior-years value override (full scale); null clears DB value */
    leaseholdPriorValue?: number | null;
}
